import React, { useState, useEffect, useMemo, useRef } from 'react';
import T from 'prop-types';
import { has } from 'lodash-es';

import CompanySelection from '../components/CompanySelection';
import SelectedCompany from '../components/SelectedCompany';
import GenerationProcess from '../components/GenerationProcess';
import ContactForm from '../components/ContactForm';
import { getAppIcons, scrollTo } from '../utils';

const FinanceFreeReportContainer = ({
  initialData: {
    initial_ticker,
    companies_list,
    recommended_companies,
    companies_options,
    sitekey,
    shared_by_report_id,
  },
}) => {
  const [step, setStep] = useState('companySelection');
  const [ticker, setTicker] = useState(initial_ticker);
  const [companies, setCompanies] = useState(companies_list);

  useEffect(() => {
    if (ticker) {
      setStep('reportGeneration');

      const selectedCompany = companies.find((company) => company.ticker === ticker);

      if (has(selectedCompany, 'icon_url')) return;

      getAppIcons(selectedCompany.id)
        .then(({ data }) => {
          const updatedCompanies = companies.map((item) => {
            if (item.ticker !== ticker) return item;
            return { ...item, ...data };
          });

          setCompanies(updatedCompanies);
        })
        .catch(() => {
          const updatedCompanies = companies.map((item) => {
            if (item.ticker !== ticker) return item;
            return { ...item, show_company_icon: true };
          });

          setCompanies(updatedCompanies);
        });
    }
  }, [ticker]);

  const handleReset = () => {
    setTicker(undefined);
    setStep('companySelection');
  };

  const company = useMemo(
    () => companies.find((item) => item.ticker === ticker) || {},
    [companies, ticker]
  );

  const reportContainerRef = useRef();

  const handleGetReport = () => {
    setStep('contactForm');
    scrollTo();
  };

  const handleReportProcessed = () => {
    scrollTo(reportContainerRef.current, -20);
  };

  return (
    <div ref={reportContainerRef} className="finance-free-report-container">
      {step === 'companySelection' && (
        <CompanySelection
          selected={ticker}
          options={companies_options}
          popularCompanies={recommended_companies}
          onChange={setTicker}
        />
      )}

      {['reportGeneration', 'contactForm'].includes(step) && (
        <SelectedCompany company={company} onReset={handleReset} />
      )}

      {step === 'reportGeneration' && (
        <GenerationProcess
          company={company}
          onGetReport={handleGetReport}
          onProcessed={handleReportProcessed}
        />
      )}

      {step === 'contactForm' && (
        <ContactForm
          sitekey={sitekey}
          company={company}
          sharedByReportId={shared_by_report_id}
          onReset={handleReset}
        />
      )}
    </div>
  );
};

FinanceFreeReportContainer.propTypes = {
  initialData: T.shape({
    initial_ticker: T.string,
    sitekey: T.string.isRequired,
    companies_list: T.array.isRequired,
    recommended_companies: T.array,
    companies_options: T.arrayOf(
      T.shape({
        text: T.string,
        value: T.string,
      })
    ).isRequired,
    shared_by_report_id: T.oneOfType([T.bool, T.number]),
  }),
};

export default FinanceFreeReportContainer;
